import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AlgorithmSelection.css";
import { logout, apiRequest } from "../utils/api";

function AlgorithmSelection() {
  const [selectedAlgorithms, setSelectedAlgorithms] = useState([]);
  const [algorithms, setAlgorithms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlgorithms();
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlgorithms = async () => {
    setLoading(true);
    try {
      const data = await apiRequest("/api/algorithms/supervised");
      if (data.status === "success" && data.algorithms) {
        // Algorithms now come as objects with name, description, and model_type
        setAlgorithms(data.algorithms);
      } else {
        console.error("Error fetching algorithms:", data.message);
        setAlgorithms([]);
      }
    } catch (error) {
      console.error("Error fetching algorithms:", error);
      setAlgorithms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  // Get algorithm name (handles both string and object formats)
  const getAlgorithmName = (algorithm) => {
    return typeof algorithm === "string" ? algorithm : algorithm.name;
  };

  // Get algorithm description
  const getAlgorithmDescription = (algorithm) => {
    return typeof algorithm === "string" ? "" : (algorithm.description || "");
  };

  // Get model type (handles both string and object formats)
  const getModelType = (algorithm) => {
    if (typeof algorithm === "object" && algorithm.model_type) {
      return algorithm.model_type;
    }
    // Fallback for string format
    const algorithmName = getAlgorithmName(algorithm);
    const regressionAlgorithms = [
      "Linear Regression",
      "Ridge Regression",
      "Lasso Regression",
    ];
    
    if (regressionAlgorithms.includes(algorithmName)) {
      return "regression";
    }
    
    const classificationAlgorithms = [
      "Logistic Regression",
      "Naive Bayes",
    ];
    
    if (classificationAlgorithms.includes(algorithmName)) {
      return "classification";
    }
    
    return "both";
  };

  const handleAlgorithmChange = (algorithm) => {
    const algorithmName = getAlgorithmName(algorithm);
    setSelectedAlgorithms((prevSelected) => {
      const prevNames = prevSelected.map(alg => getAlgorithmName(alg));
      if (prevNames.includes(algorithmName)) {
        return prevSelected.filter((item) => getAlgorithmName(item) !== algorithmName);
      } else {
        return [...prevSelected, algorithmName]; // Store just the name for submission
      }
    });
  };

  const handleSubmit = () => {
    if (selectedAlgorithms.length === 0) {
      alert("Please select at least one algorithm.");
      return;
    }
    localStorage.setItem("selectedAlgorithms", JSON.stringify(selectedAlgorithms));
    localStorage.setItem("triggerTraining", "true");
    navigate("/results");
  };

  return (
    <div className="algorithm-selection">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/supervised")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Select Algorithms</h1>
          <p className="header-subtitle">
            Select one or more algorithms to evaluate. Cross-validation will be used to assess their performance.
          </p>
          {selectedAlgorithms.length > 0 && (
            <div className="selection-counter">
              {selectedAlgorithms.length} algorithm{selectedAlgorithms.length !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading algorithms...</p>
          </div>
        ) : (
          <div className="algorithm-grid">
            {algorithms.map((algorithm, index) => {
              const algorithmName = getAlgorithmName(algorithm);
              const algorithmDesc = getAlgorithmDescription(algorithm);
              const isSelected = selectedAlgorithms.some(alg => getAlgorithmName(alg) === algorithmName);
              const modelType = getModelType(algorithm);
              return (
                <div
                  key={index}
                  className={`algorithm-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleAlgorithmChange(algorithm)}
                >
                  <div className="card-checkbox">
                    {isSelected && <div className="checkmark"></div>}
                  </div>
                  <div className="card-content">
                    <div className="card-header">
                      <h3>{algorithmName}</h3>
                      <span className={`model-type-badge ${modelType}`}>
                        {modelType === "classification" && "Classification"}
                        {modelType === "regression" && "Regression"}
                        {modelType === "both" && "Both"}
                      </span>
                    </div>
                    {algorithmDesc && (
                      <p className="card-description">{algorithmDesc}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="info-box">
          <h3>About Algorithm Selection</h3>
          <p>
            Select one or more machine learning algorithms to evaluate on your healthcare dataset. 
            Each selected algorithm will be trained and evaluated using 5-fold cross-validation, 
            which splits your data into 5 parts and tests the model on each part. This ensures 
            robust and reliable performance assessment by testing on multiple data combinations.
          </p>
          <p style={{ marginTop: "12px" }}>
            <strong>Model Types:</strong> The system automatically detects whether your target variable 
            requires classification (predicting categories like disease presence) or regression 
            (predicting continuous values like patient age or treatment duration). Algorithms marked 
            as "Both" will automatically adapt to your data type. Each algorithm card shows a brief 
            description to help you choose the best options for your analysis.
          </p>
          <p style={{ marginTop: "12px" }}>
            <strong>Performance Metrics:</strong> After evaluation, you'll see comprehensive results 
            including accuracy, precision, recall, F1-score, ROC curve, and confusion matrix for 
            classification tasks. For regression tasks, results include R² score, Mean Absolute Error 
            (MAE), Root Mean Squared Error (RMSE), and visualization plots comparing predicted vs 
            actual values. These metrics help you identify the most accurate algorithm for your 
            specific healthcare data analysis needs.
          </p>
        </div>

        {/* Action Button */}
        <div className="action-section">
          <button 
            className="submit-button" 
            onClick={handleSubmit}
            disabled={selectedAlgorithms.length === 0}
          >
            {selectedAlgorithms.length === 0 
              ? "Select at least one algorithm to continue"
              : `View Results (${selectedAlgorithms.length} selected)`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AlgorithmSelection;
